import { GetServerSideProps } from "next";
import { ProductsMasonry } from "~/components/ProductsMasonry";
import { ProductsSlider } from "~/components/ProductsSlider";
import { SectionHeading } from "~/components/SectionHeading";
import { handleRequestError } from "~/utils/rollbar";
import { StrapiProduct, StrapiStore, StrapiSubCategory } from "~/shared-types";
import {
  defaultMediaFields,
  findRandomProducts,
  findRandomProductsBySubCategory,
  findStoresByMainCategories,
} from "~/strapi/api";
import { getRoute } from "~/utils/getRoute";
import { StoreInfo } from "./StoreInfo";
import Link from "next/link";
import { StoresSlider } from "~/components/StoresSlider";
import { ProductInfo } from "~/components/ProductInfo";
import { useSingleStoreAtom } from "~/atoms/singleStore";
import { getProduct } from "~/api/products";

type ServerSideProps = {
  product: StrapiProduct;
  subCategoryProducts: StrapiProduct[];
  randomProducts: StrapiProduct[];
  similarStores: StrapiStore[];
};

export const getServerSideProps: GetServerSideProps<
  ServerSideProps,
  { productId: string }
> = async (context) => {
  const { productId } = context.params ?? {};

  if (!productId) return { notFound: true };

  const { data: product } = await getProduct(productId, {}).catch(
    handleRequestError(context.req, { data: null })
  );
  if (!product || !product.attributes.store.data) return { notFound: true };

  const storeId = product.attributes.store.data.id;
  const randomProducts = await findRandomProducts(storeId)?.catch(
    handleRequestError(context.req, [])
  );

  const subCategoryProductsQueryParams = {
    subCategoryId: product.attributes.subCategories.data[0].id,
    limit: 12,
  };
  const subCategoryProducts = await findRandomProductsBySubCategory(
    subCategoryProductsQueryParams
  ).catch(handleRequestError(context.req, []));

  const mainCategoryIds = product.attributes.subCategories.data
    .flatMap((subCategory) => subCategory.attributes.mainCategory?.data?.id)
    .filter(Boolean) as number[];

  const similarStores = await findStoresByMainCategories(mainCategoryIds, {
    populate: {
      storeImage: { fields: defaultMediaFields },
    },
    fields: ["name", "companyAddress"],
  })?.catch(handleRequestError(context.req, []));

  return {
    props: { product, subCategoryProducts, randomProducts, similarStores },
  };
};

const ProductPage = ({
  product,
  subCategoryProducts,
  randomProducts,
  similarStores,
}: ServerSideProps) => {
  const { attributes } = product;
  const store = attributes.store.data;
  const { isLimited } = useSingleStoreAtom();

  return (
    <>
      <ProductInfo product={product} />
      <StoreInfo className="mb-28" store={store} />
      <RandomProductSlider store={store} products={randomProducts} />
      {!isLimited && (
        <>
          <CategoryProducts
            category={attributes.subCategories.data[0]}
            products={subCategoryProducts}
          />
          <OtherStores similarStores={similarStores} currentStore={store} />
        </>
      )}
    </>
  );
};

type OtherStoresProps = {
  similarStores: StrapiStore[] | null;
  currentStore: StrapiStore | null;
};

export function OtherStores({ similarStores, currentStore }: OtherStoresProps) {
  if (!similarStores || similarStores.length === 0 || !currentStore)
    return null;
  const stores = similarStores.filter(
    (store) => store.id !== currentStore.id
  ) as StrapiStore[];
  return (
    <>
      <SectionHeading>
        <SectionHeading.Title>
          Andre butikker du vil synes om
        </SectionHeading.Title>
        <SectionHeading.Subtitle className="mb-20">
          Disse butikker har nogle af de samme varer som{" "}
          {currentStore.attributes.name}
        </SectionHeading.Subtitle>
      </SectionHeading>
      <StoresSlider stores={stores} className="mb-20" />
    </>
  );
}

type CategoryProductsProps = {
  category: StrapiSubCategory;
  products: StrapiProduct[];
};

export function CategoryProducts({
  category,
  products,
}: CategoryProductsProps) {
  const mainCategory = category?.attributes.mainCategory?.data;
  if (!mainCategory || products?.length === 0) return null;
  return (
    <>
      <SectionHeading>
        <SectionHeading.Title className="mb-5">
          Mere fra kategorien{" "}
          <Link
            href={getRoute(
              "/categories/:categoryId/:categoryName/:subCategoryId/:subCategoryName",
              {
                categoryId: mainCategory.id,
                categoryName: mainCategory.attributes.name,
                subCategoryId: category.id,
                subCategoryName: category.attributes.name,
              }
            )}
            className="text-primary"
          >
            {category.attributes.name}
          </Link>
        </SectionHeading.Title>
        <SectionHeading.Subtitle className="mb-20">
          Gå på udflugt i kategorien og find flere perler.
        </SectionHeading.Subtitle>
      </SectionHeading>
      <ProductsMasonry products={products} className="mb-28" />
    </>
  );
}

type RandomProductSliderProps = {
  store: StrapiStore | null;
  products: StrapiProduct[];
};

export function RandomProductSlider({
  store,
  products,
}: RandomProductSliderProps) {
  if (!store || products?.length === 0) return null;
  return (
    <div className="mb-28">
      <SectionHeading>
        <SectionHeading.Title className="mb-5">
          Mere fra{" "}
          <Link
            href={getRoute("/stores/:id", {
              id: store.id,
            })}
            className="text-primary"
          >
            {store.attributes.name}
          </Link>
        </SectionHeading.Title>
        <SectionHeading.Subtitle className="mb-16">
          Her er et udpluk af spændende produkter fra samme sælger.
        </SectionHeading.Subtitle>
      </SectionHeading>
      <ProductsSlider products={products} className="mb-20" />
    </div>
  );
}

export default ProductPage;
